/**
 * Drawing Rendering Coordinate & Bounding Box Calculations.
 *
 * Translates chart coordinates (time, price) into pixel segments and bounding boxes for canvas rendering.
 */

export interface ChartPoint {
  time: number;
  price: number;
}

export interface PixelPoint {
  x: number;
  y: number;
}

export interface RectanglePixelBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type CoordToPixelFn = (time: number, price: number) => PixelPoint | null;

/**
 * Resolves start and end pixel coordinates for a two-point segment (e.g. trendlines, rays).
 */
export function resolveSegmentPixelPoints(
  p1: ChartPoint | null | undefined,
  p2: ChartPoint | null | undefined,
  coordToPixel: CoordToPixelFn,
): { p1: PixelPoint; p2: PixelPoint } | null {
  if (!p1 || !p2 || typeof coordToPixel !== "function") return null;
  const pix1 = coordToPixel(p1.time, p1.price);
  const pix2 = coordToPixel(p2.time, p2.price);
  if (!pix1 || !pix2) return null;
  return { p1: pix1, p2: pix2 };
}

/**
 * Resolves top-left corner, width, and height for rectangle shapes from two opposing corner chart coordinates.
 */
export function resolveRectanglePixelBounds(
  p1: ChartPoint | null | undefined,
  p2: ChartPoint | null | undefined,
  coordToPixel: CoordToPixelFn,
): RectanglePixelBounds | null {
  const pts = resolveSegmentPixelPoints(p1, p2, coordToPixel);
  if (!pts) return null;
  const x = Math.min(pts.p1.x, pts.p2.x);
  const y = Math.min(pts.p1.y, pts.p2.y);
  const width = Math.abs(pts.p2.x - pts.p1.x);
  const height = Math.abs(pts.p2.y - pts.p1.y);
  return { x, y, width, height };
}
